import React, {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from "react-router-dom";
import {faChevronLeft, faRotateRight} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getPendingHotspotHabitatFeedback} from "../../../api/hotspot-habitat-feedback-pending";
import {
    moderateHotspotHabitatFeedback,
    type HotspotHabitatFeedbackModerationAction
} from "../../../api/hotspot-habitat-feedback-moderate";
import {buildHotspotDetailPath} from "../helpers";
import {HABITAT_REVIEW_DEFAULT_REASON, HABITAT_REVIEW_FAMILY_OPTIONS} from "./constants";
import {buildInitialHabitatReviewDraft, getHabitatReviewItemKey} from "./helpers";
import type {HabitatReviewDraft, HabitatReviewDraftByKey, HabitatReviewItem} from "./types";
import './habitat-review.scss';

export function HabitatReviewView() {
    const navigate = useNavigate();
    const [items, setItems] = useState<HabitatReviewItem[]>([]);
    const [draftsByKey, setDraftsByKey] = useState<HabitatReviewDraftByKey>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionKey, setActionKey] = useState<string>();

    const pendingCount = items.length;
    const uniqueHotspotCount = useMemo(
        () => new Set(items.map(item => item.locationId)).size,
        [items]
    );
    const totalFeedbackCount = useMemo(
        () => items.reduce((sum, item) => sum + item.suggestionCount, 0),
        [items]
    );

    const loadItems = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await getPendingHotspotHabitatFeedback();
            const nextItems = response.items ?? [];
            setItems(nextItems);
            setDraftsByKey(
                Object.fromEntries(nextItems.map(item => [getHabitatReviewItemKey(item), buildInitialHabitatReviewDraft(item)]))
            );
        } catch (loadError) {
            console.error(loadError);
            setError(loadError instanceof Error ? loadError.message : 'Could not load habitat review queue.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadItems();
    }, []);

    const updateDraft = (itemKey: string, field: keyof HabitatReviewDraft, value: string) => {
        setDraftsByKey(current => ({
            ...current,
            [itemKey]: {
                ...current[itemKey],
                [field]: value
            }
        }));
    };

    const submitAction = async (item: HabitatReviewItem, action: HotspotHabitatFeedbackModerationAction) => {
        const itemKey = getHabitatReviewItemKey(item);
        const draft = draftsByKey[itemKey] ?? buildInitialHabitatReviewDraft(item);
        setActionKey(`${action}:${itemKey}`);
        setError('');

        try {
            await moderateHotspotHabitatFeedback({
                action,
                feedbackIds: item.feedbackIds,
                ...(action === 'applied'
                    ? {
                        locationId: item.locationId,
                        hotspotName: item.hotspotName,
                        overrideHabitat: draft.overrideHabitat.trim(),
                        overrideFamily: draft.overrideFamily,
                        overrideDisplayHabitat: draft.overrideDisplayHabitat.trim() || draft.overrideHabitat.trim(),
                        overrideBaseHabitat: draft.overrideBaseHabitat.trim() || draft.overrideHabitat.trim(),
                        reason: draft.reason.trim() || HABITAT_REVIEW_DEFAULT_REASON,
                        evidenceSummary: {
                            source: 'human_review',
                            suggestionCount: item.suggestionCount,
                            suggestedHabitat: item.suggestedHabitat,
                            suggestedRuleLabel: item.suggestedRuleLabel,
                            predictedHabitat: item.predictedHabitat,
                            predictedFamily: item.predictedFamily
                        }
                    }
                    : {})
            });

            setItems(current => current.filter(currentItem => getHabitatReviewItemKey(currentItem) !== itemKey));
            setDraftsByKey(current => {
                const next = {...current};
                delete next[itemKey];
                return next;
            });
        } catch (submitError) {
            console.error(submitError);
            setError(submitError instanceof Error ? submitError.message : 'Could not update habitat feedback.');
        } finally {
            setActionKey(undefined);
        }
    };

    return (
        <section className="habitat-review">
            <header className="habitat-review__hero">
                <button type="button" className="habitat-review__back" onClick={() => navigate('/hotspot-explorer')}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    <span>Back to Hotspot Explorer</span>
                </button>
                <p className="habitat-review__eyebrow">Habitat Review</p>
                <h1 className="habitat-review__title">Moderate Habitat Feedback</h1>
                <p className="habitat-review__intro">
                    Review grouped hotspot confirmations and corrections, apply human overrides when needed, or dismiss bad
                    suggestions without changing the raw classifier output.
                </p>
                <div className="habitat-review__stats" aria-label="Habitat review summary">
                    <div className="habitat-review__stat">
                        <strong>{pendingCount.toLocaleString()}</strong>
                        <span>Pending suggestions</span>
                    </div>
                    <div className="habitat-review__stat">
                        <strong>{uniqueHotspotCount.toLocaleString()}</strong>
                        <span>Hotspots in queue</span>
                    </div>
                    <div className="habitat-review__stat">
                        <strong>{totalFeedbackCount.toLocaleString()}</strong>
                        <span>Feedback rows</span>
                    </div>
                </div>
            </header>

            <div className="habitat-review__content">
                <div className="habitat-review__toolbar">
                    <span className="habitat-review__status">
                        {loading ? 'Loading pending habitat feedback...' : `Showing ${items.length.toLocaleString()} grouped review item${items.length === 1 ? '' : 's'}`}
                    </span>
                    <button
                        type="button"
                        className="habitat-review__action habitat-review__action--neutral"
                        onClick={() => void loadItems()}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                        <span>Refresh</span>
                    </button>
                </div>

                {error ? (
                    <div className="habitat-review__error" role="alert">{error}</div>
                ) : null}

                {loading ? (
                    <div className="habitat-review__empty">Loading review queue...</div>
                ) : items.length ? (
                    <div className="habitat-review__queue">
                        {items.map(item => {
                            const itemKey = getHabitatReviewItemKey(item);
                            const draft = draftsByKey[itemKey] ?? buildInitialHabitatReviewDraft(item);
                            const isConfirmItem = item.feedbackType === 'confirm';
                            const isApplying = actionKey === `applied:${itemKey}`;
                            const isReviewing = actionKey === `reviewed:${itemKey}`;
                            const isDismissing = actionKey === `dismissed:${itemKey}`;
                            const isBusy = isApplying || isReviewing || isDismissing;

                            return (
                                <article className="habitat-review__card" key={itemKey}>
                                    <div className="habitat-review__card-header">
                                        <div>
                                            <h3>{item.hotspotName || item.locationId}</h3>
                                            <p>{item.locationId} | Classifier v{item.classifierVersion}</p>
                                        </div>
                                        <div className="habitat-review__count">{item.suggestionCount.toLocaleString()}</div>
                                    </div>

                                    <div className="habitat-review__meta">
                                        <div className="habitat-review__meta-block">
                                            <span>Feedback type</span>
                                            <strong>{isConfirmItem ? 'Confirm' : 'Correction'}</strong>
                                        </div>
                                        <div className="habitat-review__meta-block">
                                            <span>Birding Habitat</span>
                                            <strong>{item.predictedDisplayHabitat || item.predictedHabitat}</strong>
                                        </div>
                                        <div className="habitat-review__meta-block">
                                            <span>Base habitat</span>
                                            <strong>{item.predictedHabitat}</strong>
                                        </div>
                                        <div className="habitat-review__meta-block">
                                            <span>Habitat family</span>
                                            <strong>{item.predictedFamily || 'Unknown'}</strong>
                                        </div>
                                        <div className="habitat-review__meta-block">
                                            <span>{isConfirmItem ? 'Confirmed habitat' : 'Suggested'}</span>
                                            <strong>{item.suggestedHabitat}</strong>
                                        </div>
                                        <div className="habitat-review__meta-block">
                                            <span>Latest feedback</span>
                                            <strong>{item.latestFeedbackAt || 'Unknown'}</strong>
                                        </div>
                                    </div>

                                    {item.latestNotes ? (
                                        <p className="habitat-review__notes">{item.latestNotes}</p>
                                    ) : null}

                                    {!isConfirmItem ? (
                                        <div className="habitat-review__form">
                                            <label className="habitat-review__field">
                                                <span>Override habitat</span>
                                                <input
                                                    className="form-control"
                                                    value={draft.overrideHabitat}
                                                    onChange={(event) => updateDraft(itemKey, 'overrideHabitat', event.target.value)}
                                                />
                                            </label>
                                            <label className="habitat-review__field">
                                                <span>Override family</span>
                                                <select
                                                    className="form-select"
                                                    value={draft.overrideFamily}
                                                    onChange={(event) => updateDraft(itemKey, 'overrideFamily', event.target.value)}
                                                >
                                                    {HABITAT_REVIEW_FAMILY_OPTIONS.map(option => (
                                                        <option value={option} key={option}>{option}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="habitat-review__field">
                                                <span>Display habitat</span>
                                                <input
                                                    className="form-control"
                                                    value={draft.overrideDisplayHabitat}
                                                    onChange={(event) => updateDraft(itemKey, 'overrideDisplayHabitat', event.target.value)}
                                                />
                                            </label>
                                            <label className="habitat-review__field">
                                                <span>Base habitat</span>
                                                <input
                                                    className="form-control"
                                                    value={draft.overrideBaseHabitat}
                                                    onChange={(event) => updateDraft(itemKey, 'overrideBaseHabitat', event.target.value)}
                                                />
                                            </label>
                                            <label className="habitat-review__field habitat-review__field--full">
                                                <span>Reason</span>
                                                <textarea
                                                    className="form-control"
                                                    rows={2}
                                                    value={draft.reason}
                                                    onChange={(event) => updateDraft(itemKey, 'reason', event.target.value)}
                                                />
                                            </label>
                                        </div>
                                    ) : null}

                                    <div className="habitat-review__actions">
                                        {!isConfirmItem ? (
                                            <button
                                                type="button"
                                                className="habitat-review__action habitat-review__action--apply"
                                                onClick={() => void submitAction(item, 'applied')}
                                                disabled={isBusy}
                                            >
                                                {isApplying ? 'Applying...' : 'Apply override'}
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            className="habitat-review__action habitat-review__action--neutral"
                                            onClick={() => void submitAction(item, 'reviewed')}
                                            disabled={isBusy}
                                        >
                                            {isReviewing ? 'Saving...' : isConfirmItem ? 'Approve confirmation' : 'Mark reviewed'}
                                        </button>
                                        <button
                                            type="button"
                                            className="habitat-review__action habitat-review__action--dismiss"
                                            onClick={() => void submitAction(item, 'dismissed')}
                                            disabled={isBusy}
                                        >
                                            {isDismissing ? 'Dismissing...' : 'Dismiss'}
                                        </button>
                                        <Link className="habitat-review__link" to={buildHotspotDetailPath(item.locationId)}>
                                            Open hotspot
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="habitat-review__empty">No pending habitat corrections right now.</div>
                )}
            </div>
        </section>
    );
}
